import * as React from "react";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";

/**
 * BrandCard Component - Light Theme Editorial Design
 *
 * Clean, minimal card component for the DeTrust platform with:
 * - Clean white backgrounds with subtle borders
 * - Light shadows instead of glass-morphism
 * - Teal and amber accent variants
 * - Subtle hover states
 * - Accessibility compliant (WCAG AA)
 *
 * Built on top of shadcn/ui Card for consistency
 */

export type BrandCardVariant =
  | "primary" // Primary card - main content
  | "glass" // Glass card - stats/metrics (now clean card)
  | "data" // Data card - financial metrics
  | "premium" // Premium card - featured assets, high-value
  | "outlined" // Outlined - secondary content
  | "sapphire"; // Sapphire variant - information/trust

export type BrandCardGlow = "none" | "subtle" | "strong";

export interface BrandCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Visual variant of the card
   */
  variant?: BrandCardVariant;

  /**
   * Whether to show shadow effect on hover
   */
  glow?: BrandCardGlow;

  /**
   * Whether to add hover effects
   */
  hoverable?: boolean;

  /**
   * Market type for color coding (optional override)
   */
  marketType?: "auction" | "uniswap" | "pendle" | "governance";
}

/**
 * BrandCard Component - Light Theme
 */
export const BrandCard = React.forwardRef<HTMLDivElement, BrandCardProps>(
  (
    {
      className,
      variant = "primary",
      glow = "none",
      hoverable = false,
      marketType,
      children,
      ...props
    },
    ref,
  ) => {
    // Light theme card variants
    const variantStyles: Record<BrandCardVariant, string> = {
      // Primary Card - clean white with border
      primary:
        "bg-card border border-border shadow-sm hover:border-primary/30",

      // Glass Card - now a clean card (no blur)
      glass: "bg-card border border-border",

      // Data Card - for financial metrics
      data: "bg-card border border-border shadow-sm",

      // Premium Card - featured with teal accent
      premium:
        "bg-card border-2 border-primary/30 shadow-md",

      // Outlined - for secondary content
      outlined:
        "bg-transparent border border-border hover:border-primary/30 hover:bg-secondary/50",

      // Sapphire - for information, trust indicators
      sapphire:
        "bg-card border border-blue-200 hover:border-blue-300",
    };

    // Market type overrides (functional color coding)
    const marketStyles: Record<string, string> = {
      auction:
        "bg-card border border-amber-200 hover:border-amber-300",
      uniswap:
        "bg-card border border-blue-200 hover:border-blue-300",
      pendle:
        "bg-card border border-purple-200 hover:border-purple-300",
      governance:
        "bg-card border border-blue-200 hover:border-blue-300",
    };

    // Subtle shadow effects
    const glowStyles: Record<BrandCardGlow, string> = {
      none: "",
      subtle:
        "hover:shadow-md transition-shadow duration-300",
      strong:
        "hover:shadow-lg transition-all duration-300",
    };

    return (
      <Card
        ref={ref}
        className={cn(
          // Base styles
          "relative overflow-hidden transition-all duration-300 ease-in-out",

          // Apply variant or market type styling
          marketType ? marketStyles[marketType] : variantStyles[variant],

          // Shadow effects
          glowStyles[glow],

          // Hover effects (subtle scale)
          hoverable &&
            "cursor-pointer hover:scale-[1.02] transition-transform duration-300",

          // Custom classes
          className,
        )}
        {...props}
      >
        {children}
      </Card>
    );
  },
);

BrandCard.displayName = "BrandCard";

/**
 * Specialized Card Variants
 */

/**
 * GlassCard - clean card for stats
 */
export const GlassCard = React.forwardRef<
  HTMLDivElement,
  Omit<BrandCardProps, "variant">
>((props, ref) => <BrandCard ref={ref} variant="glass" {...props} />);

GlassCard.displayName = "GlassCard";

/**
 * PremiumCard - for featured assets, high-value content
 */
export const PremiumCard = React.forwardRef<
  HTMLDivElement,
  Omit<BrandCardProps, "variant">
>((props, ref) => (
  <BrandCard ref={ref} variant="premium" glow="subtle" {...props} />
));

PremiumCard.displayName = "PremiumCard";

/**
 * StatCard - Financial Metrics Display
 */
export interface StatCardProps extends BrandCardProps {
  /**
   * Stat label
   */
  label: string;

  /**
   * Stat value
   */
  value: string | number;

  /**
   * Optional icon
   */
  icon?: React.ReactNode;

  /**
   * Optional change indicator
   */
  change?: {
    value: number;
    period?: string;
  };

  /**
   * Trend direction (overrides change calculation)
   */
  trend?: "up" | "down" | "neutral";

  /**
   * Visual selected state - shows ring + shadow + scale effect
   */
  selected?: boolean;
}

export const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
  (
    {
      label,
      value,
      icon,
      change,
      trend,
      selected,
      className,
      children,
      variant = "glass",
      ...props
    },
    ref,
  ) => {
    // Determine trend
    const trendDirection =
      trend ||
      (change && change.value >= 0 ? "up" : change ? "down" : "neutral");
    const isPositive = trendDirection === "up";
    const isNegative = trendDirection === "down";

    return (
      <BrandCard
        ref={ref}
        variant={variant}
        className={cn(
          "p-6 group",
          // Selected state styles
          selected && [
            "ring-2 ring-primary/60",
            "scale-[1.02]",
            "shadow-md",
            "border-primary/50",
          ],
          className,
        )}
        glow={selected ? "strong" : "subtle"}
        hoverable
        {...props}
      >
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-muted-foreground font-medium uppercase tracking-wide">
            {label}
          </span>
          {icon && (
            <div className="text-primary group-hover:scale-110 transition-transform duration-300">
              {icon}
            </div>
          )}
        </div>

        <div className="text-3xl font-bold text-foreground font-mono tracking-tight tabular-nums">
          {value}
        </div>

        {change && (
          <div
            className={cn(
              "text-xs mt-2 font-semibold",
              isPositive && "text-success",
              isNegative && "text-red-600",
              !isPositive && !isNegative && "text-muted-foreground",
            )}
          >
            {isPositive ? "+" : ""}
            {change.value.toFixed(2)}%
            {change.period && (
              <span className="ml-1 text-muted-foreground/70 font-normal">
                {change.period}
              </span>
            )}
          </div>
        )}

        {children}
      </BrandCard>
    );
  },
);

StatCard.displayName = "StatCard";

/**
 * MarketCard - for different market types
 */
export interface MarketCardProps extends Omit<BrandCardProps, "marketType"> {
  marketType: NonNullable<BrandCardProps["marketType"]>;
}

export const MarketCard = React.forwardRef<HTMLDivElement, MarketCardProps>(
  ({ marketType, className, children, ...props }, ref) => {
    return (
      <BrandCard
        ref={ref}
        marketType={marketType}
        className={cn("p-6 rounded-xl", className)}
        glow="subtle"
        hoverable
        {...props}
      >
        {children}
      </BrandCard>
    );
  },
);

MarketCard.displayName = "MarketCard";

/**
 * FeatureCard - for highlighting features, trust indicators
 */
export interface FeatureCardProps extends BrandCardProps {
  /**
   * Feature title
   */
  title: string;

  /**
   * Feature description
   */
  description: string;

  /**
   * Optional icon
   */
  icon?: React.ReactNode;
}

export const FeatureCard = React.forwardRef<HTMLDivElement, FeatureCardProps>(
  ({ title, description, icon, className, children, ...props }, ref) => {
    return (
      <BrandCard
        ref={ref}
        variant="outlined"
        className={cn("p-6 rounded-xl", className)}
        hoverable
        glow="subtle"
        {...props}
      >
        {icon && (
          <div className="mb-4 text-primary w-12 h-12 flex items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
            {icon}
          </div>
        )}

        <h3 className="text-xl font-semibold text-foreground mb-2 tracking-wide">
          {title}
        </h3>

        <p className="text-muted-foreground text-sm leading-relaxed">
          {description}
        </p>

        {children}
      </BrandCard>
    );
  },
);

FeatureCard.displayName = "FeatureCard";

/**
 * DataCard - for financial data display
 */
export interface DataCardProps extends BrandCardProps {
  /**
   * Card title
   */
  title?: string;

  /**
   * Optional header action (e.g., menu, link)
   */
  headerAction?: React.ReactNode;
}

export const DataCard = React.forwardRef<HTMLDivElement, DataCardProps>(
  ({ title, headerAction, className, children, ...props }, ref) => {
    return (
      <BrandCard
        ref={ref}
        variant="data"
        className={cn("p-6 rounded-lg", className)}
        {...props}
      >
        {(title || headerAction) && (
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
            {title && (
              <h3 className="text-lg font-semibold text-foreground">{title}</h3>
            )}
            {headerAction && (
              <div className="text-muted-foreground">{headerAction}</div>
            )}
          </div>
        )}
        {children}
      </BrandCard>
    );
  },
);

DataCard.displayName = "DataCard";
